import { assert, IGNORED, subTestContext, Test } from "oc-e2e-testrunner";
import { assertSegmentIstLückenlosGefüllt, assertWarnungen, resetArrangement } from "./utils";
import { Segment } from "../arrangement/Segment";
import { ZElement } from "../arrangement/ZElement";
import { Konfiguration } from "../torkonfigurator/Konfiguration";
import { Pfosten } from "../arrangement/Pfosten";
import { RULER_FONT_HEIGHT } from "../arrangement/AbschnittRuler";

export default {
  name: "TestSchiebetor",
};

function assertSchiebetorInSzene(element: ZElement, s: Segment) {
  assert.isTrue(s.getElemente().includes(element), "Das Schiebetor wurde gelöscht");
  assert.isTrue(element?.isSchiebetor, "ist kein Schiebetor");
}

export const testEinsetzenInStrecke5m: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [4]);
  agt.update3d();
  agt.setSelectedObjects(agt.getEmpties());
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Schiebetor_Easy");
  await agt.changeSelectedZaunElemente({
    massAuswahlwert,
  });
  agt.update3d();
  const s = agt.segmente[0];
  assertSchiebetorInSzene(agt.getTore()[0], s);
  assertWarnungen(agt, []);
  assertSegmentIstLückenlosGefüllt(s);
  assert.deepEquals(
    [
      {
        length: 4.1,
        style: "Segment",
      },
      {
        length: 0.05,
        style: "Überstand",
      },
      {
        length: 0.05,
        style: "Überstand",
      },
      {
        length: 5.75,
        style: "Schiebetorüberstand",
      },
    ],
    mapRuler(s)
  );
};

const mapRuler = (s: Segment) => s.ruler.map(({ x0, x1, style }) => ({ length: x1 - x0, style }));

export const testEinsetzenInStrecke10m: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [10]);
  agt.update3d();
  agt.setSelectedObjects(agt.getEmpties());
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Schiebetor_Easy");
  await agt.changeSelectedZaunElemente({
    massAuswahlwert,
  });
  agt.update3d();
  const s = agt.segmente[0];
  const tor = agt.getTore()[0];
  assertSchiebetorInSzene(tor, s);
  assert.equals(4, tor.lichteWeite, "Lichteweite");
  assert.equals(4, tor.breite, "Breite");
  assertWarnungen(agt, []);
  assert.deepEquals(
    [
      {
        length: 4.2,
        style: "Segment",
      },
      {
        length: 0.05,
        style: "Überstand",
      },
      {
        length: 10.05,
        style: "GanzesSegment",
      },
      {
        length: 5.88,
        style: "Schiebetorüberstand",
      },
    ],
    mapRuler(s)
  );
};

export const testEinsetzenInStrecke1m: Test = async () => {
  // Ein Schiebetor hat ein Mindestmaß von 2 m und passt damit nicht in eine 1 m-Strecke.
  // Korrektes Verhalten: Das Tor wird mit seiner Mindestbreite (2 m) eingesetzt und steht über
  // die Strecke hinaus – ein lückenloses Füllen des Segments ist daher nicht möglich.
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [1]);
  agt.update3d();
  agt.setSelectedObjects(agt.getEmpties());
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Schiebetor_Easy");
  await agt.changeSelectedZaunElemente({
    massAuswahlwert,
  });
  agt.update3d();
  const s = agt.segmente[0];
  assertSchiebetorInSzene(agt.getTore()[0], s);
  // Das Tor behält sein Mindestmaß und steht über (wird nicht auf die Streckenbreite gestaucht).
  assert.equals(2, agt.getTore()[0].breite);
  assertWarnungen(agt, []);
  assert.deepEquals(
    [
      {
        length: 2.2,
        style: "Segment",
      },
      {
        length: 1.15,
        style: "Überstand",
      },
      {
        length: 0.05,
        style: "Überstand",
      },
      {
        length: 3.28,
        style: "Schiebetorüberstand",
      },
    ],
    mapRuler(s)
  );
};

export const testSchiebetorPfostenRahmenFarbeSync: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [10]);
  agt.update3d();
  agt.setSelectedObjects(agt.getEmpties());
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Schiebetor_Easy");
  await agt.changeSelectedZaunElemente({ massAuswahlwert });
  agt.update3d();

  const schiebetor = agt.getTore()[0];
  assertSchiebetorInSzene(schiebetor, agt.segmente[0]);

  const schiebetorpfosten = agt.getPfosten().filter((p) => p.isSchiebetorpfosten);
  assert.isTrue(schiebetorpfosten.length > 0, "Es sollte mindestens einen Schiebetorpfosten geben");

  const anthrazitPfostenfarbe = APP.richMetadata.findAuswahlwert("Anthrazit");
  const silberPfostenfarbe = APP.richMetadata.findAuswahlwert("Silber");
  const rahmenAnthrazit = APP.richMetadata.findAuswahlwert("Schiebetor_Rahmen_Anthrazit");
  const rahmenSilber = APP.richMetadata.findAuswahlwert("Schiebetor_Rahmen_Silber");
  assert.isTrue(
    !!anthrazitPfostenfarbe && !!silberPfostenfarbe,
    "Pfostenfarbe-Auswahlwerte Anthrazit/Silber müssen existieren"
  );
  assert.isTrue(
    !!rahmenAnthrazit && !!rahmenSilber,
    "Rahmen-Auswahlwerte Schiebetor_Rahmen_Anthrazit/Silber müssen existieren"
  );

  // 1) Pfostenfarbe ändern -> Rahmenfarbe muss synchron mitziehen
  schiebetorpfosten.forEach((p) => (p.pfostenfarbeAuswahlwert = silberPfostenfarbe));
  agt.update3d();
  assert.equals(
    rahmenSilber.Key,
    schiebetor.rahmenAuswahlwert?.Key,
    "Rahmenfarbe sollte mit der Pfostenfarbe (Silber) synchronisiert sein"
  );
  schiebetorpfosten.forEach((p) =>
    assert.equals(
      silberPfostenfarbe.Key,
      p.pfostenfarbeAuswahlwert?.Key,
      "Pfostenfarbe des Schiebetorpfostens sollte Silber sein"
    )
  );

  // 2) Pfostenfarbe zurück auf Anthrazit -> Rahmenfarbe muss mitziehen
  schiebetorpfosten.forEach((p) => (p.pfostenfarbeAuswahlwert = anthrazitPfostenfarbe));
  agt.update3d();
  assert.equals(
    rahmenAnthrazit.Key,
    schiebetor.rahmenAuswahlwert?.Key,
    "Rahmenfarbe sollte synchron auf Anthrazit gewechselt haben"
  );

  // 3) Rahmenfarbe ändern -> Pfostenfarbe-Getter muss synchron mitziehen
  schiebetor.rahmenAuswahlwert = rahmenSilber;
  agt.update3d();
  schiebetorpfosten.forEach((p) =>
    assert.equals(
      silberPfostenfarbe.Key,
      p.pfostenfarbeAuswahlwert?.Key,
      "Pfostenfarbe sollte aus Rahmenfarbe (Silber) abgeleitet sein"
    )
  );

  // 4) Rahmenfarbe zurück auf Anthrazit -> Pfostenfarbe-Getter muss synchron mitziehen
  schiebetor.rahmenAuswahlwert = rahmenAnthrazit;
  agt.update3d();
  schiebetorpfosten.forEach((p) =>
    assert.equals(
      anthrazitPfostenfarbe.Key,
      p.pfostenfarbeAuswahlwert?.Key,
      "Pfostenfarbe sollte aus Rahmenfarbe (Anthrazit) abgeleitet sein"
    )
  );
};

export const testSchiebetorZubehoer: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [4]);
  agt.update3d();
  agt.setSelectedObjects(agt.getEmpties());
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Schiebetor_Easy");
  await agt.changeSelectedZaunElemente({
    massAuswahlwert,
  });
  agt.update3d();

  const tor = agt.getTore()[0];
  agt.setSelectedObjects([tor]);
  // Mock getCurrentElement since we are using Arrangement, not ArrangementMass
  (agt as any).getCurrentElement = () => tor;
  (agt as any).getCurrentElementEinzeltor = () => tor;
  (agt as any).getSegment = () => tor.segment;

  const konf = new Konfiguration(agt as any);
  (agt as any).konfiguration = konf;
  // Setting up the Workflow in the Konfiguration since we are not going through UI
  konf.setAuswahlwert(
    "Workflow",
    APP.metadata.Workflow_Auswahlwert.find((w) => w.Key === "WORKFLOW_Schiebetor")
  );

  assert.isTrue(konf.isSchiebetor(), "isSchiebetor sollte true sein");
  assert.isTrue(
    konf.isTorWorkflow(),
    "Schiebetor sollte als TorWorkflow gelten, damit ZubehörOptionen generiert werden"
  );

  const zubehoerAuswahlwerte = APP.metadata.Auswahlwert.filter(
    (aw) => aw.Auswahl.Key == "E_Antrieb_Zubehör"
  );
  assert.isTrue(zubehoerAuswahlwerte.length > 0, "Es sollte E-Antrieb Zubehör geben");

  // Test if Lichtschranke is available (example from Zubehoer.csv)
  const lichtschranke = zubehoerAuswahlwerte.find((aw) => aw.Name === "Lichtschranke");
  assert.isTrue(!!lichtschranke, "Lichtschranke sollte als Zubehör verfügbar sein");
};

/**
 * Baut eine 8m-Strecke auf, die mit einem Profil-Element gefüllt wird, und wandelt
 * dann das linkeste Element zu einem Schiebetor und das angrenzende rechte Element
 * zu einem Einzeltor um. Konfiguriert die übergebenen Parameter und gibt die
 * relevanten Objekte zurück.
 */
async function setupSchiebetorMitEinzeltorRechts({
  schieberichtung = "Schieberichtung_rechts",
  motorseite = "Schiebetormotorseite_A",
  einzeltorAnschlag = "Anschlag_links",
  einzeltorÖffnung = "Öffnung_nach_innen",
}: Partial<{
  schieberichtung: string;
  motorseite: string;
  einzeltorAnschlag: string;
  einzeltorÖffnung: string;
}> = {}) {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [8]);
  agt.update3d();
  const segment = agt.segmente[0];
  const profil = agt.createElementFromJson({
    füllungAuswahlwert: "SYSTEM WPC CLASSIC Anthrazit",
  });
  segment.getEmpties()[0].fillWithElements(profil);
  agt.update3d();

  // linkestes Element zu Schiebetor umwandeln
  APP.arrangement.setSelectedObjects([segment.getElemente()[0]]);
  await agt.changeSelectedZaunElemente({
    massAuswahlwert: APP.richMetadata.findAuswahlwert("Schiebetor_Easy"),
  });
  agt.update3d();

  // Schiebetor konfigurieren
  const schiebetor = agt.getTore().find((t) => t.isSchiebetor);
  schiebetor.schieberichtungAuswahlwert = APP.richMetadata.findAuswahlwert(schieberichtung);
  schiebetor.schiebetormotorseiteAuswahlwert = APP.richMetadata.findAuswahlwert(motorseite);
  agt.update3d();

  // Element direkt rechts neben dem Schiebetor zu Einzeltor umwandeln
  const eleAfter = segment.getElemente();
  const sIdx = eleAfter.findIndex((e) => e.isSchiebetor);
  APP.arrangement.setSelectedObjects([eleAfter[sIdx + 1]]);
  await agt.changeSelectedZaunElemente({
    massAuswahlwert: APP.richMetadata.findAuswahlwert("Einzeltor_Easy"),
    schaffePlatzFallsDoppeltor: false,
  });
  agt.update3d();

  // Einzeltor konfigurieren
  const einzeltor = agt.getTore().find((t) => !t.isSchiebetor);
  einzeltor.anschlagAuswahlwert = APP.richMetadata.findAuswahlwert(einzeltorAnschlag);
  einzeltor.öffnungAuswahlwert = APP.richMetadata.findAuswahlwert(einzeltorÖffnung);
  agt.update3d();

  return { agt, segment, schiebetor, einzeltor };
}

function assertSchiebetorpfostenAtSchiebetor(schiebetor: ZElement, message = "") {
  const sides: Array<["pfostenLinks" | "pfostenRechts", Pfosten]> = [
    ["pfostenLinks", schiebetor.pfostenLinks],
    ["pfostenRechts", schiebetor.pfostenRechts],
  ];
  for (const [name, pfosten] of sides) {
    assert.isTrue(
      pfosten?.isSchiebetorpfosten === true,
      `${message}schiebetor.${name} soll Schiebetorpfosten sein, ist aber Pfostentyp=${(pfosten?.meta as any)?.Pfostentyp?.Key} (Key=${pfosten?.meta?.Key})`
    );
  }
}

export const testSchiebetorMitEinzeltorRechtsLegaleAnordnung: Test = async () => {
  // Schiebetor schiebt nach rechts -> Anschlag/Einzelpfosten ist rechts -> grenzt
  // an Einzeltor. Das ist die zulässige Anordnung.
  const { schiebetor, einzeltor } = await setupSchiebetorMitEinzeltorRechts({
    schieberichtung: "Schieberichtung_rechts",
  });
  assert.isTrue(
    schiebetor.pfostenRechts === einzeltor.pfostenLinks,
    "Schiebetor und Einzeltor müssen denselben mittleren Pfosten teilen"
  );
  assertSchiebetorpfostenAtSchiebetor(schiebetor);
  assert.equals(
    "Schiebetoreinzelpfosten_Anthrazit",
    schiebetor.pfostenRechts?.meta?.Key,
    "Mittlerer Pfosten muss der Einzel-Schiebetorpfosten sein (Anschlagseite)"
  );
};

export const testSchiebetorMitEinzeltorRechtsIllegaleAnordnung: Test = async () => {
  // Schiebetor schiebt nach links -> Anschlag/Einzelpfosten ist links, Doppelpfosten
  // ist rechts -> grenzt an Einzeltor. Diese Kombination ist geometrisch ungültig
  // (Schiebetor-Öffnungsbereich kollidiert mit Einzeltor), aber selbst hier MUSS
  // weiterhin ein Schiebetorpfosten verwendet werden, nicht ein normaler Torpfosten.
  const { agt, schiebetor } = await setupSchiebetorMitEinzeltorRechts({
    schieberichtung: "Schieberichtung_links",
  });
  assertSchiebetorpfostenAtSchiebetor(schiebetor);
  // Es muss eine Warnung geben, da Einzeltor an Doppel-Schiebetorpfosten liegt
  assert.isTrue(
    agt.warnungen.some((w) => w.text.includes("Öffnungsrichtung des Schiebetores")),
    "Es muss eine Warnung erscheinen, dass an der Schiebetor-Öffnungsseite kein Tor stehen darf"
  );
};

export const testSchiebetorMitEinzeltorRechtsAlleKombinationen: Test = async () => {
  // Iteriert über die Kombinationen aus Schieberichtung, Motorseite und
  // Einzeltor-Konfiguration. Egal in welcher Kombination muss der Pfosten
  // am Schiebetor immer ein Schiebetorpfosten bleiben (nicht durch normalen
  // Torpfosten ersetzt werden).
  const schieberichtungen = ["Schieberichtung_rechts", "Schieberichtung_links"];
  const motorseiten = ["Schiebetormotorseite_A", "Schiebetormotorseite_B"];
  const einzeltorAnschläge = ["Anschlag_links", "Anschlag_rechts"];
  const einzeltorÖffnungen = ["Öffnung_nach_innen", "Öffnung_nach_außen"];

  for (const schieberichtung of schieberichtungen) {
    for (const motorseite of motorseiten) {
      for (const anschlag of einzeltorAnschläge) {
        for (const öffnung of einzeltorÖffnungen) {
          const ctx = `${schieberichtung}/${motorseite}/Einzeltor:${anschlag}/${öffnung}`;
          await subTestContext(ctx, async () => {
            const { schiebetor } = await setupSchiebetorMitEinzeltorRechts({
              schieberichtung,
              motorseite,
              einzeltorAnschlag: anschlag,
              einzeltorÖffnung: öffnung,
            });
            assertSchiebetorpfostenAtSchiebetor(schiebetor, `[${ctx}] `);
          });
        }
      }
    }
  }
};

export const testEinsetzenInRechteck22m: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.inspiration = APP.metadata.Inspiration.find(
    (i) => i.Inspiration.Key == "Inspiration SYSTEM RHOMBUS HOLZ Thermo"
  );
  agt.update3d();
  const breite = 22.42;
  agt.grundriss.setStandardForm("Rechteck", [breite, breite], [true, true, true, true]);
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  agt.setSelectedObjects(agt.getElemente()[17]);
  const massAuswahlwert = APP.richMetadata.findAuswahlwert("Schiebetor_Easy");
  await agt.changeSelectedZaunElemente({
    massAuswahlwert,
  });
  agt.update3d();
  const tor = agt.getTore()[0];
  const s = agt.segmente[1];
  assertSchiebetorInSzene(tor, s);
  assert.equals(3.54, tor.lichteWeite, "Lichteweite");
  assert.equals(3.54, tor.breite, "Breite");
  assertWarnungen(agt, []);
  assertSegmentIstLückenlosGefüllt(s);
  assert.deepEquals(
    [
      {
        length: 22.5,
        style: "Segment",
      },
      {
        length: 5.71,
        style: "Element",
      },
      {
        length: 13.05,
        style: "Element",
      },
      {
        length: 3.74,
        style: "Tor",
      },
      {
        length: 5.282,
        style: "Schiebetorüberstand",
      },
    ],
    mapRuler(s)
  );
  agt.update3d();
  agt.verteileBauseitsKürzbareElementeGleichmäßig(s.getElemente().slice(0, 1));
  agt.update3d();
  assertWarnungen(agt, []);
  assertSegmentIstLückenlosGefüllt(s);
  agt.verteileBauseitsKürzbareElementeGleichmäßig(s.getElemente().slice(-1));
  agt.update3d();
  assertWarnungen(agt, []);
  assertSegmentIstLückenlosGefüllt(s);
};

/**
 * Findet Paare von Rulern auf einem Segment, deren x-Intervalle sich überschneiden und deren
 * Z-Offset-Differenz (versatz) kleiner als die Label-Schrifthöhe ist – in diesem Fall überdecken
 * sich die Maßlabels gegenseitig.
 */
function findUeberlappendeRulerLabels(s: Segment, fontHeight = RULER_FONT_HEIGHT, epsilon = 0.01) {
  const rulers = s.ruler.map((r) => ({
    style: r.style,
    a: Math.min(r.x0, r.x1),
    b: Math.max(r.x0, r.x1),
    versatz: r.versatz,
  }));
  const kollisionen: string[] = [];
  for (let i = 0; i < rulers.length; i++) {
    for (let j = i + 1; j < rulers.length; j++) {
      const r1 = rulers[i];
      const r2 = rulers[j];
      const ueberlappung = Math.min(r1.b, r2.b) - Math.max(r1.a, r2.a);
      const zDelta = Math.abs(r1.versatz - r2.versatz);
      if (ueberlappung > epsilon && zDelta < fontHeight - epsilon) {
        kollisionen.push(
          `${r1.style}(versatz=${r1.versatz}) und ${r2.style}(versatz=${r2.versatz}): ` +
            `x-Überlappung=${ueberlappung.toFixed(3)} m, z-Delta=${zDelta.toFixed(3)} < ` +
            `fontHeight=${fontHeight}`
        );
      }
    }
  }
  return kollisionen;
}

/**
 * Reproduziert den Bug, bei dem in einer mit Zaunelementen gefüllten 20 m-Strecke ein
 * mittleres Element zu einem Schiebetor gemacht wird: Der Schiebetorüberstand-Ruler und die
 * Element-Abschnitt-Ruler lagen auf demselben festen Z-Offset (0.7) und überdeckten sich.
 *
 * Der Test validiert das korrekte Verhalten: Liegen auf einem Segment Ruler unterschiedlichen
 * Typs mit überlappenden x-Intervallen, müssen ihre Z-Offsets so gestapelt sein, dass die
 * Differenz mindestens der Label-Schrifthöhe entspricht (sonst überdecken sich die Labels).
 */
export const testSchiebetorRulerStapelnSichInGefuellterStrecke: Test = async () => {
  resetArrangement();
  const agt = APP.arrangement;
  agt.grundriss.setStandardForm("Strecke", [20]);
  // 20 m-Strecke lückenlos mit normalen Zaunelementen füllen
  agt.segmente[0].parts = [];
  agt.fillIndividuellSegmentsWithElements();
  agt.update3d();
  const s = agt.segmente[0];
  assertSegmentIstLückenlosGefüllt(s);

  // ein Element in der Mitte zu einem Schiebetor umwandeln
  const elemente = s.getElemente();
  assert.isTrue(elemente.length >= 3, "Die 20 m-Strecke muss mit mehreren Elementen gefüllt sein");
  const mittleresElement = elemente[Math.floor(elemente.length / 2)];
  APP.arrangement.setSelectedObjects([mittleresElement]);
  await agt.changeSelectedZaunElemente({
    massAuswahlwert: APP.richMetadata.findAuswahlwert("Schiebetor_Easy"),
  });
  agt.update3d();

  const schiebetor = agt.getTore().find((t) => t.isSchiebetor);
  assertSchiebetorInSzene(schiebetor, s);

  // Voraussetzung: Es gibt sowohl Element- als auch Schiebetorüberstand-Ruler, deren x-Intervalle
  // sich überschneiden – nur dann ist die Stapel-Logik überhaupt relevant.
  const hatElementRuler = s.ruler.some((r) => r.style === "Element");
  const hatSchiebetorüberstandRuler = s.ruler.some((r) => r.style === "Schiebetorüberstand");
  assert.isTrue(
    hatElementRuler && hatSchiebetorüberstandRuler,
    "Erwarte sowohl Element- als auch Schiebetorüberstand-Ruler auf dem Segment"
  );

  const kollisionen = findUeberlappendeRulerLabels(s);
  assert.isTrue(
    kollisionen.length === 0,
    "Ruler-Labels dürfen sich nicht überdecken. Gefundene Kollisionen:\n" + kollisionen.join("\n")
  );
};
